/**
 * FeedSelectionBar — Multi-select toolbar for Explore & SurpriseMe pages
 * ──────────────────────────────────────────────────────────────────────────
 * Provides the same multi-select → fork / collect workflow that exists on
 * the map, but works in any feed/card context. Self-contained state.
 *
 * Usage:
 *   const sel = useFeedSelection();
 *   // On card click: sel.toggle(project)
 *   // On card render: sel.isSelected(project.id) for highlight ring
 *   // Render: <FeedSelectionBar {...sel} />
 *
 * When user taps "Fork" or "Save", navigates to Dashboard with the
 * selected project IDs encoded in the URL so Dashboard can open the
 * correct overlay with those projects pre-loaded.
 *
 * Place at: src/components/FeedSelectionBar.jsx
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, GitFork, FolderTree, CheckSquare, Minus,
} from 'lucide-react';
import { cn } from '../utils/cn';

// ═══════════════════════════════════════════════════════════════
// HOOK — useFeedSelection
// ═══════════════════════════════════════════════════════════════

export function useFeedSelection() {
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [active, setActive] = useState(false);

  const toggle = useCallback((project) => {
    if (!project?.id) return;
    setSelectedIds(prev => {
      if (prev.includes(project.id)) {
        setSelectedProjects(sp => sp.filter(p => p.id !== project.id));
        const next = prev.filter(id => id !== project.id);
        if (next.length === 0) setActive(false);
        return next;
      }
      setSelectedProjects(sp => [...sp, project]);
      setActive(true);
      return [...prev, project.id];
    });
  }, []);

  const isSelected = useCallback((id) => selectedIds.includes(id), [selectedIds]);

  const clear = useCallback(() => {
    setSelectedIds([]);
    setSelectedProjects([]);
    setActive(false);
  }, []);

  const enterMultiSelect = useCallback(() => setActive(true), []);

  return {
    selectedIds,
    selectedProjects,
    active,
    toggle,
    isSelected,
    clear,
    enterMultiSelect,
    count: selectedIds.length,
  };
}

// ═══════════════════════════════════════════════════════════════
// COMPONENT — FeedSelectionBar
// ═══════════════════════════════════════════════════════════════

export default function FeedSelectionBar({
  selectedIds,
  selectedProjects,
  active,
  count,
  clear,
}) {
  const navigate = useNavigate();

  if (!active || count === 0) return null;

  const handleFork = () => {
    const ids = selectedIds.join(',');
    navigate(`/dashboard?multiselect=${ids}&action=fork`);
  };

  const handleCollect = () => {
    const ids = selectedIds.join(',');
    navigate(`/dashboard?multiselect=${ids}&action=collect`);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 animate-in slide-in-from-bottom-4 duration-300">
      <div className="mx-auto max-w-2xl px-4 pb-4">
        <div
          className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-zinc-700/60 shadow-2xl shadow-black/40"
          style={{ background: 'rgba(24,24,27,0.97)', backdropFilter: 'blur(20px)' }}
        >
          {/* Selected project thumbnails */}
          <div className="flex items-center gap-1.5 flex-1 min-w-0 overflow-x-auto scrollbar-none">
            {selectedProjects.slice(0, 6).map(p => {
              const img = p.image_url || p.imageUrl;
              return (
                <div key={p.id} className="w-8 h-8 rounded-lg flex-shrink-0 overflow-hidden border border-zinc-600/50 bg-zinc-800">
                  {img ? (
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs font-bold text-zinc-500">
                      {(p.name || '?')[0].toUpperCase()}
                    </div>
                  )}
                </div>
              );
            })}
            {count > 6 && (
              <span className="text-[11px] text-zinc-500 flex-shrink-0 pl-1">+{count - 6}</span>
            )}
            <span className="text-[12px] text-zinc-400 flex-shrink-0 pl-2">
              {count} selected
            </span>
          </div>

          {/* Actions */}
          {count >= 2 && (
            <button
              onClick={handleFork}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold text-white
                         bg-purple-600 hover:bg-purple-500 transition-all active:scale-[0.97] flex-shrink-0"
            >
              <GitFork className="w-3.5 h-3.5" />
              Fork ({count})
            </button>
          )}

          {count >= 1 && (
            <button
              onClick={handleCollect}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-medium text-zinc-300
                         bg-zinc-800 border border-zinc-700 hover:border-zinc-600 transition-all active:scale-[0.97] flex-shrink-0"
            >
              <FolderTree className="w-3.5 h-3.5" />
              Save
            </button>
          )}

          <button
            onClick={clear}
            className="p-2 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 rounded-lg transition-colors flex-shrink-0"
            title="Clear selection"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}